import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resultsService, type ScoreHistoryEntry } from '../../services/results.service';
import { LineChart, TrendingUp, TrendingDown, Minus, Target } from 'lucide-react';

interface Point {
  date: string;
  avgScore: number;
  count: number;
  examTypes: string[];
}

function historyToPoints(entries: ScoreHistoryEntry[]): Point[] {
  if (!entries.length) return [];

  const sorted = [...entries].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const grouped: Record<string, { scores: number[]; types: Set<string> }> = {};
  sorted.forEach((e) => {
    const day = new Date(e.date).toISOString().split('T')[0];
    if (!grouped[day]) grouped[day] = { scores: [], types: new Set() };
    grouped[day].scores.push(e.percentage ?? 0);
    if (e.exam?.type) grouped[day].types.add(e.exam.type);
  });

  return Object.entries(grouped).map(([date, { scores, types }]) => ({
    date,
    avgScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    count: scores.length,
    examTypes: Array.from(types),
  }));
}

export const ProgressChart: React.FC = () => {
  const { t } = useTranslation();
  
  // Populate exam type labels from i18n
  const examTypeLabels: Record<string, string> = {
    reading: t('dashboard.sections.reading.title'),
    writing: t('dashboard.sections.writing.title'),
    listening: t('dashboard.sections.listening.title'),
    speaking: t('dashboard.sections.speaking.title'),
  };
  const [data, setData] = useState<Point[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    resultsService
      .getScoreHistory()
      .then((entries) => setData(historyToPoints(entries)))
      .catch(() => setData([]));
  }, []);

  // Empty state
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <LineChart className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t('dashboard.progressChart.title')}</h2>
            <p className="text-sm text-gray-500">{t('dashboard.progressChart.subtitle')}</p>
          </div>
        </div>
        <div className="h-48 flex flex-col items-center justify-center text-gray-400 gap-2">
          <Target className="w-10 h-10 text-gray-300" />
          <p className="text-sm">{t('examHistory.noResults')}</p>
        </div>
      </div>
    );
  }

  // Fixed 0-100% scale so scores are always in context
  const yMin = 0;
  const yMax = 100;
  const yTicks = [0, 25, 50, 75, 100];

  // Trend calculation
  const firstScore = data[0].avgScore;
  const lastScore = data[data.length - 1].avgScore;
  const trend = lastScore - firstScore;
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-500' : 'text-gray-500';
  const overallAvg = Math.round(data.reduce((sum, d) => sum + d.avgScore, 0) / data.length);

  // Chart coordinates
  const toX = (i: number) => data.length === 1 ? 50 : (i / (data.length - 1)) * 100;
  const toY = (score: number) => 100 - ((score - yMin) / (yMax - yMin)) * 100;

  const polylinePoints = data.map((p, i) => `${toX(i)},${toY(p.avgScore)}`).join(' ');
  const areaPoints = `0,100 ${data.map((p, i) => `${toX(i)},${toY(p.avgScore)}`).join(' ')} ${toX(data.length - 1)},100`;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <LineChart className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t('dashboard.progressChart.title')}</h2>
            <p className="text-sm text-gray-500">{t('dashboard.progressChart.subtitle')}</p>
          </div>
        </div>
        {data.length > 1 && (
          <div className={`inline-flex items-center gap-1 text-sm font-medium ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
            <span>{trend > 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </div>

      {/* Summary stats row */}
      <div className="flex gap-4 mb-4 mt-2">
        <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 text-center">
          <p className="text-xs text-gray-500">{t('dashboard.progressChart.subtitle')}</p>
          <p className="text-lg font-bold text-gray-900">{overallAvg}%</p>
        </div>
        <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 text-center">
          <p className="text-xs text-gray-500">{data.length === 1 ? t('dashboard.progressChart.best') : t('dashboard.progressChart.highest')}</p>
          <p className="text-lg font-bold text-green-600">{Math.max(...data.map(d => d.avgScore))}%</p>
        </div>
        <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 text-center">
          <p className="text-xs text-gray-500">{t('dashboard.stats.exams')}</p>
          <p className="text-lg font-bold text-primary">{data.reduce((s, d) => s + d.count, 0)}</p>
        </div>
      </div>

      {/* Chart area */}
      <div className="relative ml-10 mr-2" style={{ height: '200px' }}>
        {/* Y-axis labels + grid lines */}
        {yTicks.map((tick) => {
          const topPercent = 100 - tick;
          return (
            <React.Fragment key={tick}>
              <div
                className="absolute left-0 right-0 border-t border-dashed border-gray-100"
                style={{ top: `${topPercent}%` }}
              />
              <span
                className="absolute text-xs text-gray-400 -translate-y-1/2"
                style={{ top: `${topPercent}%`, left: '-2.5rem' }}
              >
                {tick}%
              </span>
            </React.Fragment>
          );
        })}

        {/* Passing score reference line at 70% */}
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-green-300/60"
          style={{ top: '30%' }}
        >
          <span className="absolute -top-4 right-0 text-[10px] text-green-500 font-medium">
            {t('dashboard.progressChart.passingScore')}
          </span>
        </div>

        {/* SVG chart */}
        <svg
          className="absolute inset-0 w-full h-full overflow-visible"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#003087" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#003087" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Area fill */}
          <polygon fill="url(#progressGradient)" points={areaPoints} />

          {/* Line */}
          <polyline
            fill="none"
            stroke="#003087"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={polylinePoints}
          />
        </svg>

        {/* Interactive data points */}
        {data.map((point, index) => {
          const xPercent = toX(index);
          const yPercent = toY(point.avgScore);
          const isHovered = hoveredIndex === index;
          const typeLabels = point.examTypes.map(et => examTypeLabels[et] || et).join(', ');

          return (
            <div
              key={point.date}
              className="absolute -ml-3 -mt-3 w-6 h-6 cursor-pointer z-10"
              style={{ left: `${xPercent}%`, top: `${yPercent}%` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Outer ring on hover */}
              <div
                className={`absolute inset-0 rounded-full bg-primary/20 transition-transform duration-200 ${
                  isHovered ? 'scale-150' : 'scale-0'
                }`}
              />
              {/* Dot */}
              <div
                className={`absolute inset-1 rounded-full border-2 border-white shadow-md transition-all duration-200 ${
                  isHovered ? 'bg-primary scale-125' : 'bg-secondary'
                }`}
              />

              {/* Tooltip */}
              {isHovered && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-20 pointer-events-none">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                    <div className="font-semibold text-sm">{point.avgScore}%</div>
                    <div className="text-gray-300 mt-0.5">
                      {new Date(point.date).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="text-gray-400 text-[10px] mt-0.5">
                      {point.count} examen{point.count > 1 ? 's' : ''} · {typeLabels}
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1" />
                </div>
              )}
            </div>
          );
        })}

        {/* Score labels on dots (always visible, no hover needed) */}
        {data.length <= 10 && data.map((point, index) => {
          const xPercent = toX(index);
          const yPercent = toY(point.avgScore);
          return (
            <span
              key={`label-${point.date}`}
              className="absolute text-[10px] font-semibold text-primary -translate-x-1/2 pointer-events-none"
              style={{ left: `${xPercent}%`, top: `${yPercent - 8}%` }}
            >
              {point.avgScore}%
            </span>
          );
        })}
      </div>

      {/* X-axis date labels */}
      <div className="ml-10 mr-2 mt-2 flex justify-between text-xs text-gray-500">
        {data.map((point, index) => (
          <span key={point.date} className="text-center" style={{
            width: data.length === 1 ? '100%' : undefined,
            textAlign: data.length === 1 ? 'center' : undefined,
          }}>
            {new Date(point.date).toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: 'short',
            })}
          </span>
        ))}
      </div>
    </div>
  );
};
